import { PlusOutlined } from '@ant-design/icons'
import { Button, Empty, Pagination } from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import queryString from 'query-string'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import { useLocation, withRouter } from 'react-router-dom'
import AccessControlAction from '../../../components/AccessControlAction'
import { AvatarHasName } from '../../../components/Common/Avatar'
import TableComponent from '../../../components/Common/TableComponent'
import PageTitle from '../../../components/PageTitle'
import UnreadText from '../../../components/UnreadText/UnreadText'
import { ACL_ACTION_TYPE, DATE_FORMAT_DEFAULT } from '../../../constants'
import BoardReportCreateModal from '../BoardReportCreate/BoardReportCreateModal'
import BoardReportLayout from '../BoardReportLayout.js'
import AdvanceFilterBlockBoardReport from './AdvanceFilterBlockBoardReport'
import {
  TableActionWrapper,
  TableFooterActionWrapper,
} from './BoardReportPageStyled'

const TIEU_DE_WIDTH = 600
const NGAY_TAO_WIDTH = 120
const NGUOI_TAO_WIDTH = 150
const SCROLL_WIDTH = 1000

const BoardReportPage = props => {
  const { history, loadingAnimationStore, boardReportStore, userStore } = props

  const {
    boardReportList,
    boardReportTotalCount,
    queryParams,
  } = boardReportStore

  const { userList } = userStore

  const [isVisibleCreateModal, setIsVisibleCreateModal] = useState(false)
  const location = useLocation()
  const queryStringParse = useMemo(() => queryString.parse(location.search), [
    location.search,
  ])
  const [isRefreshPage, setIsRefreshPage] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)

  const formatName = useCallback(name => {
    if (!name || typeof name !== 'string') return ''
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }, [])

  const stripHtml = useCallback(html => {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }, [])

  const renderTitleColumn = useCallback(
    record => <UnreadText text={record.title} width={TIEU_DE_WIDTH} />,
    []
  )

  const renderDateColumn = useCallback(
    record => (
      <UnreadText
        text={moment(record.created_at).format(DATE_FORMAT_DEFAULT)}
        width={NGAY_TAO_WIDTH}
      />
    ),
    []
  )

  const renderUserColumn = useCallback(
    record => {
      const userCreated = record.user_created

      return (
        <UnreadText
          text={
            <div className="user">
              {userCreated ? (
                <AvatarHasName
                  imgId={userCreated.image_id}
                  name={formatName(
                    userCreated.name_lowercase || userCreated.user_name
                  )}
                />
              ) : (
                <span
                  style={{
                    color: 'rgba(146, 142, 142, 0.8)',
                    display: 'inline-block',
                    minWidth: '70px',
                    fontStyle: 'italic',
                  }}>
                  Không có.
                </span>
              )}
            </div>
          }
          width={NGUOI_TAO_WIDTH}
        />
      )
    },
    [formatName]
  )

  const tableColumns = useMemo(
    () => [
      {
        title: 'Tiêu đề',
        width: TIEU_DE_WIDTH,
        render: renderTitleColumn,
      },
      {
        title: 'Ngày tạo',
        width: NGAY_TAO_WIDTH,
        render: renderDateColumn,
      },
      {
        title: 'Người tạo',
        width: NGUOI_TAO_WIDTH,
        render: renderUserColumn,
      },
    ],
    [renderTitleColumn, renderDateColumn, renderUserColumn]
  )

  const onChangePagination = useCallback(
    pageIndex => {
      boardReportStore.setQueryParams({
        ...queryParams,
        page: pageIndex - 1,
        size: 10,
      })
      const queryStringStringify = queryString.stringify(
        {
          ...queryStringParse,
          page: pageIndex - 1,
          size: 10,
        },
        {
          skipNull: true,
        }
      )
      history.replace(`/board-report?${queryStringStringify}`)
    },
    [queryParams, queryStringParse, history, boardReportStore]
  )

  const getBoardReportList = useCallback(async () => {
    try {
      loadingAnimationStore.setTableLoading(true)
      await boardReportStore.getBoardReportList()

      const keyword = (queryParams.content || '').toLowerCase().trim()
      if (keyword) {
        const filteredList = boardReportStore.boardReportList.filter(item => {
          const plainText = stripHtml(item.content).toLowerCase()
          return plainText.includes(keyword)
        })

        boardReportStore.boardReportList = filteredList
      }
    } catch (error) {
      console.log(error)
    } finally {
      loadingAnimationStore.setTableLoading(false)
    }
  }, [queryParams, boardReportStore, loadingAnimationStore, stripHtml])

  const handleRowClick = useCallback(
    record => ({
      onClick: () => history.push(`/board-report/${record.code}`),
    }),
    [history]
  )

  const handleCreateModalOpen = useCallback(() => {
    setIsVisibleCreateModal(true)
  }, [])

  const handleCreateModalClose = useCallback(() => {
    setIsVisibleCreateModal(false)
  }, [])

  const handleCreateSuccess = useCallback(() => {
    getBoardReportList()
  }, [getBoardReportList])

  const tableLocale = useMemo(
    () => ({
      emptyText: (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={'Không có dữ liệu'}
        />
      ),
    }),
    []
  )

  const tableLoading = useMemo(
    () => ({
      tip: 'Đang tải báo cáo...',
      spinning: loadingAnimationStore.tableLoading,
    }),
    [loadingAnimationStore.tableLoading]
  )

  const tableScroll = useMemo(() => ({ x: SCROLL_WIDTH }), [])

  useEffect(() => {
    boardReportStore.setQueryParams({
      ...queryParams,
      ...queryStringParse,
    })
    ;(async () => {
      try {
        loadingAnimationStore.setTableLoading(true)
        await getBoardReportList()
        setIsRefreshPage(false)
        setIsInitialized(true)
      } catch (err) {
        console.log(err)
      } finally {
        loadingAnimationStore.setTableLoading(false)
      }
    })()

    return () => {
      boardReportStore.clearBoardReportList()
      boardReportStore.clearQueryParams()
    }
  }, [])

  useEffect(() => {
    if (isRefreshPage || !isInitialized) return
    ;(async () => {
      await getBoardReportList()
    })()
  }, [queryParams, isInitialized, isRefreshPage, getBoardReportList])

  useEffect(() => {
    if (!userList || userList.length === 0) {
      userStore.getUserListV2()
    }
  }, [userList, userStore])

  const renderPageTitle = useMemo(
    () => (
      <PageTitle location={props.location} title="Văn bản đến" hiddenGoBack>
        <AccessControlAction
          aclActionType={ACL_ACTION_TYPE.create__REPORT_HDQT}>
          <Button
            icon={<PlusOutlined />}
            onClick={handleCreateModalOpen}
            type="primary">
            Tạo mới
          </Button>
        </AccessControlAction>
      </PageTitle>
    ),
    [props.location, handleCreateModalOpen]
  )

  return (
    <>
      <BoardReportLayout title={renderPageTitle}>
        <Helmet>
          <title>Báo cáo HĐQT</title>
        </Helmet>
        <TableActionWrapper>
          <div style={{ marginBottom: 24 }}>
            <AdvanceFilterBlockBoardReport userList={userList} />
          </div>

          <TableComponent
            onRow={handleRowClick}
            rowKey={record => record.id}
            dataSource={boardReportList}
            columns={tableColumns}
            pagination={false}
            scroll={tableScroll}
            locale={tableLocale}
            loading={tableLoading}
          />
          <TableFooterActionWrapper>
            <Pagination
              onChange={onChangePagination}
              total={boardReportTotalCount}
              pageSize={queryParams.size}
              current={queryParams.page + 1}
              showSizeChanger={false}
              showLessItems
            />
          </TableFooterActionWrapper>
        </TableActionWrapper>
      </BoardReportLayout>

      <BoardReportCreateModal
        visible={isVisibleCreateModal}
        onCancel={handleCreateModalClose}
        onSuccess={handleCreateSuccess}
      />
    </>
  )
}

export default memo(
  withRouter(
    inject(
      'boardReportStore',
      'userStore',
      'loadingAnimationStore'
    )(observer(BoardReportPage))
  )
)
